import { useQuery } from '@tanstack/react-query';

import { withError } from '@/react-tools/react-query';
import axios, { parseAxiosError } from '@/portainer/services/axios/axios';
import { EnvironmentId } from '@/react/portainer/environments/types';

import {
  TRIVY_API,
  TRIVY_LABELS,
  VulnerabilityReport,
  VulnerabilityReportList,
} from '../types';

import { queryKeys } from './queryKeys';

// Addressed by label selector, not name: report names are derived
// ({kind}-{resource}-{container}) and awkward to reconstruct. Needs read access
// to aquasecurity.github.io, which today only admins have.
export function useVulnerabilityReports<T = Array<VulnerabilityReport>>({
  environmentId,
  namespace,
  resourceName,
  resourceKind,
  containerName,
  enabled = true,
  select,
}: {
  environmentId: EnvironmentId;
  namespace?: string;
  resourceName?: string;
  resourceKind?: string;
  containerName?: string;
  enabled?: boolean;
  select?: (reports: Array<VulnerabilityReport>) => T;
}) {
  return useQuery(
    queryKeys.vulnerabilityReports(
      environmentId,
      namespace,
      resourceName,
      resourceKind,
      containerName
    ),
    () =>
      getVulnerabilityReports(
        environmentId,
        namespace,
        resourceName,
        resourceKind,
        containerName
      ),
    {
      ...withError('Unable to get vulnerability reports'),
      // The hover summary and the details modal have nowhere to show a failure,
      // so this one still notifies — but once, not after four attempts.
      retry: false,
      enabled: enabled && !!environmentId,
      select,
    }
  );
}

async function getVulnerabilityReports(
  environmentId: EnvironmentId,
  namespace?: string,
  resourceName?: string,
  resourceKind?: string,
  containerName?: string
): Promise<Array<VulnerabilityReport>> {
  const scope = namespace ? `namespaces/${namespace}/` : '';
  const selectors = [
    resourceName && `${TRIVY_LABELS.resourceName}=${resourceName}`,
    // A Deployment/foo and a StatefulSet/foo can share a namespace, and trivy
    // writes one report per container, so name alone selects other workloads'
    // reports as well as the other containers of this one.
    resourceKind && `${TRIVY_LABELS.resourceKind}=${resourceKind}`,
    containerName && `${TRIVY_LABELS.containerName}=${containerName}`,
  ].filter(Boolean);
  const params = selectors.length
    ? { labelSelector: selectors.join(',') }
    : undefined;

  try {
    const { data } = await axios.get<VulnerabilityReportList>(
      `/endpoints/${environmentId}/kubernetes/${TRIVY_API}/${scope}vulnerabilityreports`,
      { params }
    );
    return data.items ?? [];
  } catch (error) {
    throw parseAxiosError(error);
  }
}
